import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  DialogTitle,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput
} from "@mui/material";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useUser } from "../../contexts/UserContext";
import PermissionsStyles from './styles/PermissionsStyles';
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";

const AddUserForm = ({ rolesList, branchesList, onAddUser, onCancel }) => {
  const [branches, setBranches] = useState(branchesList);
  const [emailError, setEmailError] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const { user: myUser } = useUser();
  const [hospitals, setHospitals] = useState([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const [hierarchyUsers, setHierarchyUsers] = useState([]);

  const [user, setUser] = React.useState({
    Name: "",
    LastName: "",
    Email: "",
    PriorityUserName: "",
    Role: [],
    Branches: [],
    Hospital: "",
    tradeDelayRole: "",
    tradeDelayParentId: "",
  });

  // בדיקה האם למשתמש יש הרשאת ChemoRecordsPage
  const hasChemoRecordsPermission = user.Role.some(roleName => {
    const role = rolesList.find(r => r.Name === roleName);
    return role && role.ChemoRecordsPage === true;
  });

  const hasTradeDelayPermission = user.Role.some(roleName => {
    const role = rolesList.find(r => r.Name === roleName);
    return role && role.TradeDelayPage === true;
  });

  // שליפת היררכיית השהיות סחר כשיש הרשאת TradeDelay
  useEffect(() => {
    if (hasTradeDelayPermission && hierarchyUsers.length === 0) {
      execute("GET", `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/tradeDelayHierarchy`)
        .then((r) => setHierarchyUsers((r?.data || []).filter((u) => u.tradeDelayRole)))
        .catch(() => {});
    }
    if (!hasTradeDelayPermission) {
      setUser((prev) => ({ ...prev, tradeDelayRole: "", tradeDelayParentId: "" }));
    }
  }, [hasTradeDelayPermission]);

  // שליפת רשימת בתי חולים מהבקאנד כשיש הרשאת ChemoRecords
  useEffect(() => {
    const fetchHospitals = async () => {
      if (hasChemoRecordsPermission && hospitals.length === 0) {
        setLoadingHospitals(true);
        try {
          const response = await execute(
            "GET",
            `${process.env.REACT_APP_BACKEND_BASE_URL}/api/users/parameter12Values`
          );
          if (response && Array.isArray(response)) {
            setHospitals(response);
          }
        } catch (error) {
          console.error("Error fetching hospitals:", error);
          // במקרה של שגיאה, נשאיר רשימה ריקה
          setHospitals([]);
        } finally {
          setLoadingHospitals(false);
        }
      }
    };

    fetchHospitals();
  }, [hasChemoRecordsPermission, execute]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const isValid =
      user.Name.trim() !== "" &&
      user.LastName.trim() !== "" &&
      user.Email.trim() !== "" &&
      user.PriorityUserName.trim() !== "" &&
      !emailError &&
      user.Role.length > 0;

    setFormIsValid(isValid);
  }, [user, emailError]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUser((prevUser) => ({ ...prevUser, [name]: value }));

    if (name === "Email") {
      setEmailError(!validateEmail(value));
    }
  };

  const handleRoleChange = (event) => {
    const selectedRoles = event.target.value;
    const newHasTradeDelay = selectedRoles.some(roleName => {
      const role = rolesList.find(r => r.Name === roleName);
      return role && role.TradeDelayPage === true;
    });
    setUser((prevUser) => ({
      ...prevUser,
      Role: selectedRoles,
      tradeDelayRole: newHasTradeDelay ? prevUser.tradeDelayRole : '',
    }));
  };

  const handleBranchChange = (event) => {
    const selectedBranches = event.target.value;
    setUser((prevUser) => ({ ...prevUser, Branches: selectedBranches }));
  };

  const handleHospitalChange = (event) => {
    const selectedHospital = event.target.value;
    setUser((prevUser) => ({ ...prevUser, Hospital: selectedHospital }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (typeof onAddUser === 'function') {
      const userToSubmit = { ...user };
      onAddUser(userToSubmit);
    }
  };

  const handleSelectAllBranches = (event) => {
    if (event.target.checked) {
      setUser({ ...user, Branches: [] });
    } else {
      setUser({ ...user, Branches: [] });
    }
  };

  const renderBranchesValue = (selected) => {
    if (selected.length === 0) {
      return <span style={{ color: '#999' }}>הרשאה לכל הסניפים</span>;
    }

    if (selected.length <= 2) {
      return selected.join(', ');
    }

    return `${selected[0]}, ${selected[1]} + ${selected.length - 2} נוספים`;
  };

  const renderRolesValue = (selected) => {
    if (selected.length === 0) {
      return <span style={PermissionsStyles.placeholderText}>בחר תפקידים...</span>;
    }

    return (
      <Box sx={PermissionsStyles.chipBox}>
        {selected.map((value) => (
          <Chip
            key={value}
            label={value}
            size="small"
            sx={PermissionsStyles.chip}
          />
        ))}
      </Box>
    );
  };

  const filteredRoles = rolesList.filter((role) =>
    myUser.roles.some(r => r.admin === true)
      ? true
      : myUser.roles.some(r => r._id === role._id)
  );

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      PaperProps={{ sx: PermissionsStyles.dialogPaperLarge }}
      maxWidth="md"
    >
      <div style={PermissionsStyles.containerLarge}>
        <DialogTitle sx={PermissionsStyles.dialogTitle}>
          <PersonAddIcon sx={PermissionsStyles.icon} />
          <Typography variant="h5" component="div" sx={PermissionsStyles.dialogTitleText}>
            הוספת משתמש חדש
          </Typography>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={PermissionsStyles.dialogContent}>
            <Grid container spacing={3} direction="column">
              <Grid item container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="שם פרטי"
                    type="text"
                    name="Name"
                    value={user.Name}
                    onChange={handleChange}
                    fullWidth
                    required
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    variant="outlined"
                    sx={PermissionsStyles.textField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="שם משפחה"
                    type="text"
                    name="LastName"
                    value={user.LastName}
                    onChange={handleChange}
                    fullWidth
                    required
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    variant="outlined"
                    sx={PermissionsStyles.textField}
                  />
                </Grid>
              </Grid>

              <Grid item container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="אימייל"
                    type="email"
                    name="Email"
                    value={user.Email.toLowerCase()}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={emailError}
                    helperText={emailError ? 'כתובת אימייל לא חוקית' : ''}
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    variant="outlined"
                    sx={emailError ? PermissionsStyles.textFieldError : PermissionsStyles.textField}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="שם משתמש"
                    name="PriorityUserName"
                    value={user.PriorityUserName?.toLowerCase()}
                    onChange={handleChange}
                    fullWidth
                    required
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    variant="outlined"
                    sx={PermissionsStyles.textField}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="roles-label" className={PermissionsStyles.rtlText}>
                    תפקידים
                  </InputLabel>
                  <Select
                    labelId="roles-label"
                    multiple
                    value={user.Role}
                    onChange={handleRoleChange}
                    required
                    renderValue={renderRolesValue}
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    input={<OutlinedInput label="תפקידים" />}
                    MenuProps={PermissionsStyles.menuPropsLarge}
                    sx={PermissionsStyles.selectMenu}
                  >
                    {filteredRoles.map((role) => (
                      <MenuItem key={role._id} value={role.Name} sx={PermissionsStyles.menuItem}>
                        <Checkbox
                          checked={user.Role.indexOf(role.Name) > -1}
                          sx={PermissionsStyles.checkbox}
                        />
                        <ListItemText primary={role.Name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="branches-label" className={PermissionsStyles.rtlText}>
                    סניפים
                  </InputLabel>
                  <Select
                    labelId="branches-label"
                    multiple
                    value={user.Branches}
                    onChange={handleBranchChange}
                    className={PermissionsStyles.rtlText}
                    dir={PermissionsStyles.rtlDir}
                    renderValue={renderBranchesValue}
                    input={<OutlinedInput label="סניפים" />}
                    MenuProps={PermissionsStyles.menuPropsLarge}
                    sx={PermissionsStyles.selectMenu}
                  >
                    <MenuItem value="בחר הכל" sx={PermissionsStyles.menuItemSelectAll}>
                      <Checkbox
                        checked={user.Branches.length === 0}
                        indeterminate={user.Branches.length > 0 && user.Branches.length < branches.length}
                        sx={PermissionsStyles.checkboxIndeterminate}
                        onChange={handleSelectAllBranches}
                      />
                      <ListItemText primary="בחר הכל" />
                    </MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.BRANCHNAME} value={branch.BRANCHNAME} sx={PermissionsStyles.menuItem}>
                        <Checkbox
                          checked={user.Branches.indexOf(branch.BRANCHNAME) > -1}
                          sx={PermissionsStyles.checkbox}
                        />
                        <ListItemText primary={branch.BRANCHNAME} />
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" sx={PermissionsStyles.caption}>
                    * השארת שדה ריק = הרשאה לכל הסניפים
                  </Typography>
                </FormControl>
              </Grid>

              {/* שדה תפקיד השהיות סחר - מופיע רק כשנבחר תפקיד עם TradeDelayPage */}
              {hasTradeDelayPermission && <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="trade-delay-role-label">תפקיד בהשהיות סחר (אופציונלי)</InputLabel>
                  <Select
                    labelId="trade-delay-role-label"
                    value={user.tradeDelayRole}
                    onChange={(e) =>
                      setUser((prev) => ({ ...prev, tradeDelayRole: e.target.value, tradeDelayParentId: "" }))
                    }
                    input={<OutlinedInput label="תפקיד בהשהיות סחר (אופציונלי)" />}
                    sx={PermissionsStyles.selectMenu}
                  >
                    <MenuItem value="">ללא תפקיד</MenuItem>
                    <MenuItem value="manager">מנהל</MenuItem>
                    <MenuItem value="employee">עובד</MenuItem>
                  </Select>
                </FormControl>
              </Grid>}

              {hasTradeDelayPermission && user.tradeDelayRole === 'employee' && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="trade-delay-parent-label">מנהל (אופציונלי)</InputLabel>
                    <Select
                      labelId="trade-delay-parent-label"
                      value={user.tradeDelayParentId}
                      onChange={(e) => setUser((prev) => ({ ...prev, tradeDelayParentId: e.target.value }))}
                      input={<OutlinedInput label="מנהל (אופציונלי)" />}
                      sx={PermissionsStyles.selectMenu}
                    >
                      <MenuItem value="">ללא ממונה</MenuItem>
                      {hierarchyUsers.filter(u => u.tradeDelayRole === 'manager').map((u) => (
                        <MenuItem key={u._id} value={u._id}>
                          {u.Name} {u.LastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* שדה בית חולים - מופיע רק למשתמשים עם הרשאת ChemoRecordsPage */}
              {hasChemoRecordsPermission && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel
                      id="hospital-label"
                      className={PermissionsStyles.rtlText}
                    >
                      {loadingHospitals ? "טוען בתי חולים..." : "בית חולים"}
                    </InputLabel>
                    <Select
                      labelId="hospital-label"
                      value={user.Hospital}
                      onChange={handleHospitalChange}
                      className={PermissionsStyles.rtlText}
                      dir={PermissionsStyles.rtlDir}
                      disabled={loadingHospitals}
                      input={<OutlinedInput label="בית חולים" />}
                      sx={PermissionsStyles.selectMenu}
                    >
                      <MenuItem value="">
                        <em>לא נבחר</em>
                      </MenuItem>
                      {loadingHospitals ? (
                        <MenuItem disabled>
                          <em>טוען...</em>
                        </MenuItem>
                      ) : (
                        hospitals.map((hospital) => (
                          <MenuItem key={hospital.SPECVALUE} value={hospital.SPECVALUE} sx={PermissionsStyles.menuItem}>
                            {hospital.SPECVALUE}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <Typography variant="caption" sx={PermissionsStyles.caption}>
                      * בחר בית חולים עבור נציג בית חולים
                    </Typography>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={PermissionsStyles.dialogActions}>
            <Button
              onClick={onCancel}
              variant="outlined"
              sx={PermissionsStyles.buttonOutlined}
            >
              ביטול
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!formIsValid}
              sx={PermissionsStyles.buttonContained}
            >
              הוסף
            </Button>
          </DialogActions>
        </form>
      </div>
    </Dialog>
  );
};

export default AddUserForm;